import type { FlowDefinition } from '../schema/flow'

/**
 * ความยาวของเส้นทางที่ "ยาวที่สุดที่ยังเหลือ" จากแต่ละโหนดไปจนจบ
 *
 * ใช้ DFS แบบสามสี (ขาว/เทา/ดำ) และ **ข้ามเส้นย้อนกลับ (back edge)** เพื่อไม่ให้
 * flow ที่วนกลับทำให้คำนวณไม่รู้จบ — เส้นย้อนกลับนับเป็น 0
 * ผลลัพธ์ถูก cache ตามตัว definition (definition ที่ publish แล้วเป็น snapshot ที่ไม่เปลี่ยน)
 */

const cache = new WeakMap<FlowDefinition, Map<string, number>>()

type Color = 0 | 1 | 2 // 0 = ยังไม่เยี่ยม, 1 = กำลังเยี่ยม (เทา), 2 = เสร็จแล้ว (ดำ)

export function computeLongestRemainingMap(def: FlowDefinition): Map<string, number> {
  const cached = cache.get(def)
  if (cached) return cached

  const nodeIds = new Set(def.nodes.map((node) => node.id))
  const isEnd = new Map(def.nodes.map((node) => [node.id, node.type === 'end'] as const))
  // ขั้นตอนที่ปิดใช้งานถูกเอ็นจินข้ามไปเอง ไม่ถือเป็น 1 ก้าวของผู้ตอบ
  const isDisabled = new Map(def.nodes.map((node) => [node.id, node.disabled] as const))

  const adjacency = new Map<string, string[]>()
  for (const node of def.nodes) adjacency.set(node.id, [])
  for (const edge of def.edges) {
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) continue
    adjacency.get(edge.source)?.push(edge.target)
  }

  const color = new Map<string, Color>()
  const longest = new Map<string, number>()

  // DFS แบบ iterative — กัน stack overflow บน flow ที่ยาวมาก
  for (const startId of nodeIds) {
    if ((color.get(startId) ?? 0) !== 0) continue

    const stack: Array<{ id: string; childIndex: number }> = [{ id: startId, childIndex: 0 }]
    color.set(startId, 1)

    while (stack.length > 0) {
      const frame = stack[stack.length - 1] as { id: string; childIndex: number }
      const children = adjacency.get(frame.id) ?? []

      if (frame.childIndex < children.length) {
        const childId = children[frame.childIndex] as string
        frame.childIndex += 1
        const childColor = color.get(childId) ?? 0
        if (childColor === 0) {
          color.set(childId, 1)
          stack.push({ id: childId, childIndex: 0 })
        }
        // childColor === 1 (เส้นย้อนกลับ) → ข้าม, childColor === 2 → ใช้ค่าที่คำนวณไว้แล้วตอนสรุป
        continue
      }

      // ลูกทุกตัวคำนวณเสร็จแล้ว → สรุปค่าของโหนดนี้
      stack.pop()
      let best = 0
      if (!(isEnd.get(frame.id) ?? false)) {
        for (const childId of children) {
          if ((color.get(childId) ?? 0) !== 2) continue // ข้ามเส้นย้อนกลับ
          const stepCost = isDisabled.get(childId) ?? false ? 0 : 1
          best = Math.max(best, stepCost + (longest.get(childId) ?? 0))
        }
      }
      longest.set(frame.id, best)
      color.set(frame.id, 2)
    }
  }

  cache.set(def, longest)
  return longest
}

export function longestRemainingPath(def: FlowDefinition, fromNodeId: string): number {
  return computeLongestRemainingMap(def).get(fromNodeId) ?? 0
}

export const MAX_PROGRESS_BEFORE_FINISH = 0.99

/**
 * คำนวณความคืบหน้า
 * `previous` ทำให้แถบไม่ถอยหลังเมื่อผู้ตอบเจอทางลัดที่ข้ามหลายขั้น
 * (ตอนกดย้อนกลับให้ส่ง previous = 0 เพื่อคำนวณใหม่ตามจริง)
 */
export function computeProgress(
  def: FlowDefinition,
  currentNodeId: string,
  stepsTaken: number,
  previous = 0,
): number {
  const remaining = longestRemainingPath(def, currentNodeId)
  const total = Math.max(stepsTaken + remaining, 1)
  const raw = Math.min(stepsTaken / total, MAX_PROGRESS_BEFORE_FINISH)
  return Math.max(previous, raw)
}
